<template>
  <div>
    <div v-if="title">
      <h2
        v-if="size === 'large'"
        class="graph-title"
      >
        {{ title }}
      </h2>
      <h3
        v-if="size === 'medium'"
        class="graph-title"
      >
        {{ title }}
      </h3>
      <h5
        v-if="size === 'small'"
        class="graph-title"
      >
        {{ title }}
      </h5>
    </div>

    <div class="grid-x legend-row">
      <div class="small-10 cell">
        <div
          :class="{
            singleMetricChartContainerLarge: size === 'large',
            singleMetricChartContainerMedium: size === 'medium',
            singleMetricChartContainerSmall: size === 'small',
          }"
          :style="{ height: chartHeight }"
        >
          <canvas :id="canvasID" />
        </div>
      </div>
      <div class="small-2 cell">
        <ul class="chart-numbers">
          <li
            v-for="(data, n) in dataToRender"
            :key="n"
            :class="{
              graphNumberLarge: size === 'large',
              graphNumberLargeMargin: size === 'large',
              graphNumberMedium: size === 'medium',
              graphNumberMediumMargin: size === 'medium',
              graphNumberSmall: size === 'small',
              graphNumberSmallMargin: size === 'small',
            }"
          >
            <span :style="{ color: textColors[n] }">{{ formatDataLabel(data) }} </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'RareSingleMetricChart',
  props: {
    chartData: {
      type: Array,
      required: true,
    },
    size: {
      type: String,
      required: false,
      default: 'large',
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    canvasID: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      chartOptions: {
        tooltips: { enabled: true },
        hover: { mode: null },
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            display: false,
            categoryPercentage: 0.4,
            barThickness: 0,
            ticks: {
              display: true,
              color: 'rgba(0, 0, 0, 0)',
              suggestedMin: 0,
              suggestedMax: 100,
            },
          }],
          xAxes: [{
            display: false,
            categoryPercentage: 0.5,
            barThickness: 0,
            ticks: {
              display: true,
              min: 0,
              max: 45433,
            },
          }],
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      chartLabels: 'legendData',
      chartColors: 'fetchBarColors',
      textColors: 'fetchTextColors',
    }),
    dataToRender() {
      let dataToReturn;
      if (this.$store.getters.industryCompAndDecimalException) {
        dataToReturn = this.chartData.slice(0, 1);
      } else {
        dataToReturn = this.chartData;
      }
      return dataToReturn;
    },
    datapoints() {
      return this.chartData.length;
    },
    chartHeight() {
      let height;
      if (this.size === 'large') {
        height = 122;
      } else if (this.size === 'medium') {
        height = 82;
      } else if (this.size === 'small') {
        height = 42;
      }
      if (this.datapoints === 4) { height *= 2; }
      if (this.$store.getters.industryCompAndDecimalException) { height *= 0.9; }
      return `${height}px`;
    },
  },
  watch: {
    chartData: function () { // eslint-disable-line
      this.buildChart();
    },
  },
  created() {
    if (this.size === 'large') {
      this.chartOptions.scales.yAxes[0].barThickness = 60;
      this.chartOptions.scales.xAxes[0].barThickness = 60;
    } else if (this.size === 'medium') {
      this.chartOptions.scales.yAxes[0].barThickness = 40;
      this.chartOptions.scales.xAxes[0].barThickness = 40;
    } else if (this.size === 'small') {
      this.chartOptions.scales.yAxes[0].barThickness = 20;
      this.chartOptions.scales.xAxes[0].barThickness = 20;
    }
  },
  mounted() {
    Chart.scaleService.updateScaleDefaults('linear', {
      ticks: {
        min: 0,
      },
    });
    if (this.size === 'small') {
      this.chartOptions.tooltips.enabled = false;
    }
    this.buildChart();
  },
  methods: {
    buildChart() {
      if (this.$store.getters.dataFormat === 'percentage') {
        this.chartOptions.scales.xAxes[0].ticks.max = this.$store.getters['$_summary/summaryPageMaxPercent'];
      } else {
        this.chartOptions.scales.xAxes[0].ticks.max = this.$store.getters['$_summary/summaryPageMaxDecimalBase'];
      }

      const singleMetricChartCTX = document.getElementById(this.canvasID).getContext('2d');
      if (!window.chart) {
        window.chart = [];
      }
      if (window.chart[this.canvasID] !== undefined) {
        window.chart[this.canvasID].destroy();
      }
      window.chart[this.canvasID] = new Chart(singleMetricChartCTX, {
        type: 'horizontalBar',
        data: {
          labels: this.chartLabels,
          datasets: [{
            data: this.dataToRender,
            backgroundColor: this.chartColors,
          }],
        },
        options: this.chartOptions,
      });
    },

    formatDataLabel(string) {
      let returnValue;
      if (string === 'N/A') {
        returnValue = string;
      } else {
        const percentage = (this.$store.getters.dataFormat === 'percentage' ? '%' : '');
        returnValue = `${string}${percentage}`;
      }
      return returnValue;
    },
  },
};
</script>

<style lang="scss" scoped>

  @import "~@/css/global.scss";

  .singleMetricChartContainerLarge {
    margin-top: 35px;
    position: relative;
    height: 280px;
    width: 600px;
  }

  .singleMetricChartContainerMedium {
    margin-top: 35px;
    position: relative;
    height: 196px;
    width: 600px;
  }

  .singleMetricChartContainerSmall {
    margin-top: 35px;
    position: relative;
    height: 96px;
    width: 600px;
  }

  .chart-numbers {
    list-style: none;
    margin-top: 35px;
  }

  .graphNumberLargeMargin {
    height: 60px;
    line-height: 60px;
    padding: 0;
    margin: 0 0 1px 0;
  }

  .graphNumberMediumMargin {
    height: 40px;
    line-height: 40px;
    padding: 0;
    margin: 0 0 1px 0;
  }

  .graphNumberSmallMargin {
    height: 20px;
    line-height: 20px;
    padding: 0;
    margin: 0 0 1px 0;
  }
</style>
